import { useEffect, useState } from "react";
import { useParams } from "react-router";
import {
  Button,
  Field,
  Input,
  makeStyles,
  Radio,
  RadioGroup,
  Select,
  shorthands,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import { AddRegular, DeleteRegular } from "@fluentui/react-icons";
import { apiUrl } from "../utils/api";
import { laenderMitBestandswert } from "../data/laender";

const BRAND = {
  fontFamilyPrimary: "'Barlow', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  fontFamilyAccent: "'Playfair Display', Georgia, 'Times New Roman', serif",
  colors: {
    pageBg: "#361936",
    primaryPurple: "#4F266B",
    accent: "#F8AA00",
    accentHover: "#F8AA00",
    cardBg: "#fbf9fc",
    border: "#4F266B",
    textOnDark: "#B4AFB7",
    muted: "#B4AFB7",
  },
};

type ExtraAddress = {
  id: number;
  label: string;
  street: string;
  city: string;
  plz: string;
  ortsteil: string;
  country: string;
};

const useStyles = makeStyles({
  page: {
    minHeight: "100vh",
    backgroundColor: BRAND.colors.pageBg,
    backgroundImage:
      "radial-gradient(120% 120% at 85% 15%, #4F266B 0%, #3f1f57 28%, #361936 62%, #361936 100%)",
    color: tokens.colorNeutralForegroundInverted,
    fontFamily: BRAND.fontFamilyPrimary,
  },
  hero: {
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    ...shorthands.padding("28px", "16px", "20px", "16px"),
    "@media (min-width: 768px)": {
      ...shorthands.padding("56px", "24px", "28px", "24px"),
    },
  },
  title: {
    marginTop: 0,
    marginBottom: "12px",
    color: BRAND.colors.accent,
    fontSize: "clamp(1.9rem, 7vw, 4.2rem)",
    lineHeight: 1.08,
    fontWeight: 400,
    fontFamily: BRAND.fontFamilyAccent,
  },
  subtitle: {
    marginTop: 0,
    marginBottom: "8px",
    color: BRAND.colors.textOnDark,
    fontSize: "clamp(0.95rem, 3.2vw, 1.15rem)",
    fontFamily: BRAND.fontFamilyPrimary,
  },
  formWrap: {
    maxWidth: "1100px",
    marginLeft: "auto",
    marginRight: "auto",
    ...shorthands.padding("8px", "16px", "28px", "16px"),
    "@media (min-width: 768px)": {
      ...shorthands.padding("16px", "24px", "40px", "24px"),
    },
  },
  formCard: {
    backgroundColor: BRAND.colors.cardBg,
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.border("1px", "solid", BRAND.colors.border),
    boxShadow: tokens.shadow16,
    ...shorthands.padding("16px"),
    fontFamily: BRAND.fontFamilyPrimary,
    "@media (min-width: 768px)": {
      ...shorthands.padding("24px"),
    },
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: "14px",
    fontSize: "clamp(1.2rem, 4vw, 1.8rem)",
    color: BRAND.colors.pageBg,
    fontFamily: BRAND.fontFamilyAccent,
    fontWeight: 400,
  },
  addressSectionCard: {
    marginTop: "22px",
    ...shorthands.border("1px", "solid", BRAND.colors.border),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding("16px"),
    backgroundColor: BRAND.colors.cardBg,
  },
  fields: {
    display: "grid",
    gap: "14px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 980px)": {
      gridTemplateColumns: "1fr 1fr",
      alignItems: "start",
    },
  },
  fullWidth: {
    width: "100%",
    fontFamily: BRAND.fontFamilyPrimary,
  },
  fullRow: {
    gridColumn: "1 / -1",
  },
  subSectionTitle: {
    marginTop: "22px",
    marginBottom: "14px",
    fontSize: "clamp(1.15rem, 3.5vw, 1.6rem)",
    color: BRAND.colors.pageBg,
    fontFamily: BRAND.fontFamilyAccent,
    fontWeight: 400,
  },
  sectionCard: {
    marginTop: "22px",
    ...shorthands.border("1px", "solid", BRAND.colors.border),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding("16px"),
    backgroundImage: "linear-gradient(135deg, #361936 0%, #4F266B 100%)",
    color: BRAND.colors.accent,
  },
  inlineTitle: {
    marginTop: 0,
    marginBottom: "10px",
    color: BRAND.colors.accent,
    fontFamily: BRAND.fontFamilyPrimary,
    fontSize: "1.05rem",
    fontWeight: 700,
  },
  radioGroup: {
    marginBottom: "0",
  },
  boxRadio: {
    color: "#ffffff",
    "& .fui-Radio__label": {
      color: "#ffffff",
    },
    "& .fui-Radio__indicator": {
      border: `1px solid ${BRAND.colors.accent}`,
    },
    "& .fui-Radio__indicator::after": {
      backgroundColor: BRAND.colors.accent,
    },
  },
  tableHead: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    marginBottom: "8px",
    gap: "12px",
    flexWrap: "wrap",
  },
  helperText: {
    marginTop: 0,
    marginBottom: "12px",
    color: BRAND.colors.accent,
    fontSize: "0.95rem",
  },
  addAddressButton: {
    ...shorthands.border("1px", "solid", BRAND.colors.accent),
    color: BRAND.colors.accent,
    backgroundColor: "transparent",
    fontWeight: 600,
    ":hover": {
      backgroundColor: "rgba(248, 170, 0, 0.12)",
      color: BRAND.colors.accent,
    },
  },
  addLabelRow: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "10px",
    "@media (min-width: 820px)": {
      gridTemplateColumns: "1fr auto",
      alignItems: "end",
    },
  },
  addressList: {
    display: "grid",
    gap: "12px",
  },
  emptyState: {
    ...shorthands.border("1px", "dashed", BRAND.colors.accent),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding("14px", "16px"),
    color: BRAND.colors.accent,
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    fontSize: "0.95rem",
  },
  addressCard: {
    marginTop: "22px",
    ...shorthands.border("1px", "solid", BRAND.colors.border),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding("16px"),
    backgroundColor: BRAND.colors.cardBg,
  },
  addressCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
    gap: "10px",
    flexWrap: "wrap",
  },
  addressCardTitle: {
    margin: 0,
    color: BRAND.colors.pageBg,
    fontWeight: 400,
    fontFamily: BRAND.fontFamilyAccent,
    fontSize: "clamp(1.05rem, 3.2vw, 1.4rem)",
  },
  cardHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  cardDeleteButton: {
    ...shorthands.border("1px", "solid", BRAND.colors.border),
    color: BRAND.colors.pageBg,
    backgroundColor: "#ffffff",
    ":hover": {
      backgroundColor: "#f3ecf8",
      color: BRAND.colors.pageBg,
    },
  },
  addressFields: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 760px)": {
      gridTemplateColumns: "1fr 1fr",
    },
    "@media (min-width: 1180px)": {
      gridTemplateColumns: "1.1fr 1.5fr 1fr 0.8fr 1fr",
    },
  },
  addressField: {
    display: "grid",
    gap: "6px",
  },
  addressFieldLabel: {
    color: BRAND.colors.pageBg,
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  actions: {
    marginTop: "20px",
    display: "flex",
    justifyContent: "flex-end",
  },
  submitButton: {
    width: "100%",
    minHeight: "44px",
    backgroundColor: BRAND.colors.accent,
    color: BRAND.colors.pageBg,
    border: `1px solid ${BRAND.colors.accent}`,
    fontFamily: BRAND.fontFamilyPrimary,
    fontWeight: 700,
    ":hover": {
      backgroundColor: BRAND.colors.accentHover,
      border: `1px solid ${BRAND.colors.accentHover}`,
      color: BRAND.colors.pageBg,
    },
    "@media (min-width: 640px)": {
      width: "auto",
      minWidth: "220px",
    },
  },
  stateCard: {
    maxWidth: "680px",
    margin: "48px auto",
    backgroundColor: BRAND.colors.cardBg,
    color: BRAND.colors.pageBg,
    ...shorthands.border("1px", "solid", BRAND.colors.border),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding("20px"),
    boxShadow: tokens.shadow8,
    textAlign: "center",
    fontFamily: BRAND.fontFamilyPrimary,
  },
});

const PublicCustomerCheckAddressForm = () => {
  const styles = useStyles();
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [submitOk, setSubmitOk] = useState(false);

  const [street, setStreet] = useState("");
  const [plz, setPlz] = useState("");
  const [city, setCity] = useState("");
  const [ortsteil, setOrtsteil] = useState("");
  const [country, setCountry] = useState("Deutschland");
  const [hasDifferentPostalAddress, setHasDifferentPostalAddress] = useState("Nein");
  const [postalStreet, setPostalStreet] = useState("");
  const [postalPlz, setPostalPlz] = useState("");
  const [postalCity, setPostalCity] = useState("");
  const [postalOrtsteil, setPostalOrtsteil] = useState("");
  const [postalCountry, setPostalCountry] = useState("Deutschland");
  const [hasMoreAddresses, setHasMoreAddresses] = useState("Nein");
  const [newAddressLabel, setNewAddressLabel] = useState("");
  const [extraAddresses, setExtraAddresses] = useState<ExtraAddress[]>([]);

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setValid(false);
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(apiUrl(`public-form/${token}`));
        setValid(res.ok);
      } catch {
        setValid(false);
      } finally {
        setLoading(false);
      }
    };
    checkToken();
  }, [token]);

  const addAddress = () => {
    const label = newAddressLabel.trim();
    if (!label) return;

    setExtraAddresses((prev) => [
      ...prev,
      {
        id: Date.now() + Math.floor(Math.random() * 1000),
        label,
        street: "",
        city: "",
        plz: "",
        ortsteil: "",
        country: "Deutschland",
      },
    ]);

    setNewAddressLabel("");
  };

  const removeAddress = (id: number) => {
    setExtraAddresses((prev) => prev.filter((addr) => addr.id !== id));
  };

  const updateAddress = (id: number, key: keyof ExtraAddress, value: string) => {
    setExtraAddresses((prev) =>
      prev.map((addr) => (addr.id === id ? { ...addr, [key]: value } : addr))
    );
  };

  const handleSubmit = async () => {
    if (!token) return;
    const payload = {
      street,
      postalCode: plz,
      city,
      ortsteil,
      country,
      hasDifferentPostalAddress,
      postalAddress:
        hasDifferentPostalAddress === "Ja"
          ? {
              street: postalStreet,
              postalCode: postalPlz,
              city: postalCity,
              ortsteil: postalOrtsteil,
              country: postalCountry,
            }
          : null,
      hasMoreAddresses,
      additionalAddresses: hasMoreAddresses === "Ja" ? extraAddresses : [],
    };

    const res = await fetch(apiUrl(`public-form/${token}`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (res.ok) setSubmitOk(true);
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>
          <Spinner label="Formular wird geladen…" />
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>Der Link ist ungültig oder abgelaufen.</div>
      </div>
    );
  }

  if (submitOk) {
    return (
      <div className={styles.page}>
        <div className={styles.stateCard}>Vielen Dank, Ihre Anschriften wurden übermittelt.</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <h1 className={styles.title}>Prüfung der Adressdaten</h1>
        <p className={styles.subtitle}>Bitte prüfen und ergänzen Sie Ihre Adressdaten.</p>
      </div>

      <div className={styles.formWrap}>
        <div className={styles.formCard}>
          <div className={styles.addressSectionCard}>
            <h2 className={styles.sectionTitle}>Unternehmenssitz</h2>

            <div className={styles.fields}>
              <Field label="Straße u. Hausnummer">
                <Input
                  className={styles.fullWidth}
                  size="large"
                  value={street}
                  placeholder="Hauptstraße 15"
                  onChange={(_, d) => setStreet(d.value)}
                />
              </Field>

              <Field label="PLZ">
                <Input
                  className={styles.fullWidth}
                  size="large"
                  value={plz}
                  placeholder="50667"
                  onChange={(_, d) => setPlz(d.value)}
                />
              </Field>

              <Field label="Ort">
                <Input
                  className={styles.fullWidth}
                  size="large"
                  value={city}
                  placeholder="Köln"
                  onChange={(_, d) => setCity(d.value)}
                />
              </Field>

              <Field label="Ortsteil">
                <Input
                  className={styles.fullWidth}
                  size="large"
                  value={ortsteil}
                  placeholder="Sülz"
                  onChange={(_, d) => setOrtsteil(d.value)}
                />
              </Field>

              <Field className={styles.fullRow} label="Land">
                <Select
                  className={styles.fullWidth}
                  size="large"
                  value={country}
                  onChange={(event) => setCountry(event.target.value)}
                >
                  {laenderMitBestandswert(country).map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </div>

          <div className={styles.sectionCard}>
            <p className={styles.inlineTitle}>Abweichende Postanschrift?</p>
            <RadioGroup
              className={styles.radioGroup}
              layout="horizontal"
              value={hasDifferentPostalAddress}
              onChange={(_, data) => setHasDifferentPostalAddress(data.value)}
            >
              <Radio className={styles.boxRadio} value="Nein" label={<span style={{ color: "#ffffff" }}>Nein</span>} />
              <Radio className={styles.boxRadio} value="Ja" label={<span style={{ color: "#ffffff" }}>Ja</span>} />
            </RadioGroup>
          </div>

          {hasDifferentPostalAddress === "Ja" && (
            <div className={styles.addressSectionCard}>
              <h2 className={styles.subSectionTitle}>Postanschrift</h2>
              <div className={styles.fields}>
                <Field label="Straße u. Hausnummer">
                  <Input
                    className={styles.fullWidth}
                    size="large"
                    value={postalStreet}
                    placeholder="Hauptstraße 15"
                    onChange={(_, d) => setPostalStreet(d.value)}
                  />
                </Field>

                <Field label="PLZ">
                  <Input
                    className={styles.fullWidth}
                    size="large"
                    value={postalPlz}
                    placeholder="50667"
                    onChange={(_, d) => setPostalPlz(d.value)}
                  />
                </Field>

                <Field label="Ort">
                  <Input
                    className={styles.fullWidth}
                    size="large"
                    value={postalCity}
                    placeholder="Köln"
                    onChange={(_, d) => setPostalCity(d.value)}
                  />
                </Field>

                <Field label="Ortsteil">
                  <Input
                    className={styles.fullWidth}
                    size="large"
                    value={postalOrtsteil}
                    placeholder="Sülz"
                    onChange={(_, d) => setPostalOrtsteil(d.value)}
                  />
                </Field>

                <Field className={styles.fullRow} label="Land">
                  <Select
                    className={styles.fullWidth}
                    size="large"
                    value={postalCountry}
                    onChange={(event) => setPostalCountry(event.target.value)}
                  >
                    {laenderMitBestandswert(postalCountry).map((item) => (
                      <option key={`postal-${item}`} value={item}>
                        {item}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            </div>
          )}

          <div className={styles.sectionCard}>
            <p className={styles.inlineTitle}>Weitere Anschriften?</p>
            <RadioGroup
              className={styles.radioGroup}
              layout="horizontal"
              value={hasMoreAddresses}
              onChange={(_, data) => setHasMoreAddresses(data.value)}
            >
              <Radio className={styles.boxRadio} value="Nein" label={<span style={{ color: "#ffffff" }}>Nein</span>} />
              <Radio className={styles.boxRadio} value="Ja" label={<span style={{ color: "#ffffff" }}>Ja</span>} />
            </RadioGroup>

            {hasMoreAddresses === "Ja" && (
              <>
                <p className={styles.helperText} style={{ marginTop: 12 }}>
                  Geben Sie zuerst die Bezeichnung der neuen Anschrift ein.
                </p>
                <div className={styles.addLabelRow}>
                  <Field label={<span style={{ color: "#ffffff" }}>Name / Bezeichner</span>}>
                    <Input
                      value={newAddressLabel}
                      placeholder="z. B. Büro Hamburg"
                      onChange={(_, d) => setNewAddressLabel(d.value)}
                    />
                  </Field>
                  <Button
                    className={styles.addAddressButton}
                    appearance="secondary"
                    icon={<AddRegular />}
                    onClick={addAddress}
                    disabled={!newAddressLabel.trim()}
                  >
                    Adresse hinzufügen
                  </Button>
                </div>
              </>
            )}
          </div>

          {hasMoreAddresses === "Ja" && (
            <div className={styles.addressList}>
              {extraAddresses.length === 0 && (
                <div className={styles.emptyState}>Noch keine zusätzliche Anschrift vorhanden.</div>
              )}

              {extraAddresses.map((addr) => (
                <div key={addr.id} className={styles.addressCard}>
                  <div className={styles.addressCardHeader}>
                    <h2 className={styles.addressCardTitle}>{addr.label}</h2>
                    <div className={styles.cardHeaderActions}>
                      <Button
                        className={styles.cardDeleteButton}
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        aria-label="Anschrift löschen"
                        onClick={() => removeAddress(addr.id)}
                      >
                        Löschen
                      </Button>
                    </div>
                  </div>

                  <div className={styles.fields}>
                    <Field label="Straße u. Hausnummer">
                      <Input
                        className={styles.fullWidth}
                        size="large"
                        value={addr.street}
                        placeholder="Hauptstraße 15"
                        onChange={(_, d) => updateAddress(addr.id, "street", d.value)}
                      />
                    </Field>

                    <Field label="PLZ">
                      <Input
                        className={styles.fullWidth}
                        size="large"
                        value={addr.plz}
                        placeholder="50667"
                        onChange={(_, d) => updateAddress(addr.id, "plz", d.value)}
                      />
                    </Field>

                    <Field label="Ort">
                      <Input
                        className={styles.fullWidth}
                        size="large"
                        value={addr.city}
                        placeholder="Köln"
                        onChange={(_, d) => updateAddress(addr.id, "city", d.value)}
                      />
                    </Field>

                    <Field label="Ortsteil">
                      <Input
                        className={styles.fullWidth}
                        size="large"
                        value={addr.ortsteil}
                        placeholder="Sülz"
                        onChange={(_, d) => updateAddress(addr.id, "ortsteil", d.value)}
                      />
                    </Field>

                    <Field className={styles.fullRow} label="Land">
                      <Select
                        className={styles.fullWidth}
                        size="large"
                        value={addr.country}
                        onChange={(event) => updateAddress(addr.id, "country", event.target.value)}
                      >
                        {laenderMitBestandswert(addr.country).map((item) => (
                          <option key={`extra-${addr.id}-${item}`} value={item}>
                            {item}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className={styles.actions}>
            <Button className={styles.submitButton} appearance="primary" onClick={handleSubmit}>
              Absenden
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicCustomerCheckAddressForm;
